import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail, downloadReport } from "@/lib/api";
import { inr, thisMonth, today } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import { SearchSelect } from "@/components/SearchSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { BadgeIndianRupee, Trash2, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function SalaryDetail() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [employeeId, setEmployeeId] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [salaryAmount, setSalaryAmount] = useState("");
  const [inHand, setInHand] = useState("");
  const [advanceTaken, setAdvanceTaken] = useState("");
  const [advancePaid, setAdvancePaid] = useState("");
  const [memo, setMemo] = useState("");
  const [filterEmp, setFilterEmp] = useState("all");

  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const { data: salaries = [] } = useQuery({
    queryKey: ["salaries", filterEmp],
    queryFn: async () => (await api.get("/salaries", { params: { employee_id: filterEmp === "all" ? undefined : filterEmp } })).data,
  });

  const pending = useMemo(() => {
    const p = Number(salaryAmount || 0) - Number(inHand || 0);
    return p > 0 ? +p.toFixed(2) : 0;
  }, [salaryAmount, inHand]);

  const onPickEmp = (id) => {
    setEmployeeId(id);
    const emp = employees.find((e) => e.id === id);
    if (emp && !salaryAmount) setSalaryAmount(emp.salary);
  };

  const save = useMutation({
    mutationFn: async () => api.post("/salaries", {
      employee_id: employeeId, month, salary_amount: Number(salaryAmount || 0),
      in_hand: Number(inHand || 0), pending, advance_taken: Number(advanceTaken || 0),
      advance_balance_paid: Number(advancePaid || 0), date: today(), memo,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salaries"] });
      toast.success("Salary recorded");
      setSalaryAmount(""); setInHand(""); setAdvanceTaken(""); setAdvancePaid(""); setMemo("");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/salaries/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salaries"] }); toast.success("Deleted"); },
  });

  return (
    <div>
      <PageTitle title="Salary Details" subtitle="Monthly salary, advances & pending tracking" />

      <div className="grid lg:grid-cols-3 gap-4">
        {isAdmin && (
        <Card className="p-5 h-fit">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><BadgeIndianRupee className="h-4 w-4 text-primary" /> Record Salary</h3>
          <div className="space-y-3">
            <div>
              <Label>Employee *</Label>
              <div className="mt-1.5">
                <SearchSelect value={employeeId} onChange={onPickEmp}
                  options={employees.map((e) => ({ value: e.id, label: `${e.employee_no} — ${e.name}` }))}
                  placeholder="Select employee" searchPlaceholder="Type employee name or code…" testid="salary-employee" />
              </div>
            </div>
            <div><Label>Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5" data-testid="salary-month" /></div>
            <div><Label>Salary Amount (₹)</Label><Input type="number" value={salaryAmount} onChange={(e) => setSalaryAmount(e.target.value)} className="mt-1.5" data-testid="salary-amount" /></div>
            <div><Label>In-Hand Paid (₹)</Label><Input type="number" value={inHand} onChange={(e) => setInHand(e.target.value)} className="mt-1.5" data-testid="salary-inhand" /></div>
            <div className="rounded-lg bg-muted/50 px-3 py-2 flex justify-between text-sm"><span className="text-muted-foreground">Pending</span><span className="font-bold data-num text-destructive" data-testid="salary-pending">{inr(pending)}</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Advance Taken</Label><Input type="number" value={advanceTaken} onChange={(e) => setAdvanceTaken(e.target.value)} className="mt-1" data-testid="salary-advance" /></div>
              <div><Label className="text-xs">Advance Paid Back</Label><Input type="number" value={advancePaid} onChange={(e) => setAdvancePaid(e.target.value)} className="mt-1" data-testid="salary-advance-paid" /></div>
            </div>
            <div><Label>Memo</Label><Textarea value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1.5" /></div>
            <Button className="w-full" onClick={() => save.mutate()} disabled={!employeeId || save.isPending} data-testid="save-salary">{save.isPending ? "Saving…" : "Record Salary"}</Button>
          </div>
        </Card>
        )}

        <Card className="lg:col-span-2 overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-3">
            <div className="w-64">
              <SearchSelect value={filterEmp} onChange={setFilterEmp}
                options={[{ value: "all", label: "All Employees" }, ...employees.map((e) => ({ value: e.id, label: `${e.employee_no} — ${e.name}` }))]}
                searchPlaceholder="Type employee name or code…" testid="salary-filter" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadReport("/reports/salaries/export", { fmt: "pdf", employee_id: filterEmp === "all" ? undefined : filterEmp }, "salaries")}><FileDown className="h-4 w-4" /> PDF</Button>
              <Button variant="outline" size="sm" onClick={() => downloadReport("/reports/salaries/export", { fmt: "excel", employee_id: filterEmp === "all" ? undefined : filterEmp }, "salaries")}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
            </div>
          </div>
          <div className="overflow-x-auto ssacc-scroll">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Employee</TableHead><TableHead>Month</TableHead><TableHead className="text-right">Salary</TableHead>
                <TableHead className="text-right">In-Hand</TableHead><TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">Advance</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {salaries.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No salary records.</TableCell></TableRow>
                ) : salaries.map((s) => (
                  <TableRow key={s.id} data-testid="salary-row">
                    <TableCell><div className="font-medium">{s.employee_name}</div><div className="text-xs text-primary">{s.employee_no}</div></TableCell>
                    <TableCell><Badge variant="secondary">{s.month}</Badge></TableCell>
                    <TableCell className="text-right data-num">{inr(s.salary_amount)}</TableCell>
                    <TableCell className="text-right data-num text-success">{inr(s.in_hand)}</TableCell>
                    <TableCell className="text-right data-num text-destructive">{inr(s.pending)}</TableCell>
                    <TableCell className="text-right data-num text-xs">{inr(s.advance_taken)} / {inr(s.advance_balance_paid)}</TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete salary record?</AlertDialogTitle><AlertDialogDescription>{s.employee_name} — {s.month}</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate(s.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
